import type { AircraftSpec, DeskRole, MelCategory, MelItem, MelSeverity } from "./types";

export const MEL_CATEGORY_LABEL: Record<MelCategory, string> = {
  powerplant: "Powerplant",
  avionics: "Avionics",
  airframe: "Airframe",
  landing_gear: "Landing gear",
  hydraulics: "Hydraulics",
  electrical: "Electrical",
  cabin: "Cabin",
  other: "Other",
};

export const MEL_SEVERITY_LABEL: Record<MelSeverity, string> = {
  info: "Info",
  defer: "Defer",
  ground: "Ground",
};

export const MEL_CATEGORIES: MelCategory[] = [
  "powerplant",
  "avionics",
  "airframe",
  "landing_gear",
  "hydraulics",
  "electrical",
  "cabin",
  "other",
];

export function newMelId(now = new Date()): string {
  return `mel-${now.getTime().toString(36)}-${Math.floor(Math.random() * 1e4).toString(36)}`;
}

export function normalizeAta(value: string): string {
  return value.trim().replace(/\s+/g, "").toUpperCase();
}

export function hydrateMelItem(raw: Partial<MelItem>): MelItem | null {
  const aircraftId = typeof raw.aircraftId === "string" ? raw.aircraftId : "";
  const description = typeof raw.description === "string" ? raw.description.trim() : "";
  if (!aircraftId || !description) return null;
  const severity: MelSeverity =
    raw.severity === "ground" || raw.severity === "defer" || raw.severity === "info" ? raw.severity : "defer";
  const category: MelCategory = MEL_CATEGORIES.includes(raw.category as MelCategory)
    ? (raw.category as MelCategory)
    : "other";
  return {
    id: raw.id || newMelId(),
    aircraftId,
    registration: (raw.registration ?? "").trim().toUpperCase(),
    ata: normalizeAta(raw.ata ?? ""),
    category,
    description,
    severity,
    status: raw.status === "closed" ? "closed" : "open",
    deferUntilUtc: raw.deferUntilUtc,
    createdAtUtc: raw.createdAtUtc || new Date().toISOString(),
    createdBy: raw.createdBy === "pilot" ? "pilot" : "dispatcher",
    closedAtUtc: raw.closedAtUtc,
    notes: (raw.notes ?? "").trim(),
  };
}

export function hydrateMelItems(list: MelItem[] | undefined): MelItem[] {
  if (!Array.isArray(list)) return [];
  return list.map(hydrateMelItem).filter((row): row is MelItem => Boolean(row));
}

export function createMelItem(input: {
  aircraft: Pick<AircraftSpec, "id" | "registration">;
  ata?: string;
  category?: MelCategory;
  description: string;
  severity: MelSeverity;
  notes?: string;
  deferUntilUtc?: string;
  role?: DeskRole;
  now?: Date;
}): MelItem {
  const now = input.now ?? new Date();
  return {
    id: newMelId(now),
    aircraftId: input.aircraft.id,
    registration: input.aircraft.registration,
    ata: normalizeAta(input.ata ?? ""),
    category: input.category ?? "other",
    description: input.description.trim() || "Deferred defect",
    severity: input.severity,
    status: "open",
    deferUntilUtc: input.deferUntilUtc,
    createdAtUtc: now.toISOString(),
    createdBy: input.role ?? "dispatcher",
    notes: (input.notes ?? "").trim(),
  };
}

export function closeMelItem(item: MelItem, now = new Date()): MelItem {
  return { ...item, status: "closed", closedAtUtc: now.toISOString() };
}

export function openMelFor(aircraftId: string, items: MelItem[]): MelItem[] {
  return items.filter((m) => m.aircraftId === aircraftId && m.status === "open");
}

/** Open ground-severity MEL blocks assign / grounds the tail. */
export function hasGroundingMel(aircraftId: string, items: MelItem[]): boolean {
  return openMelFor(aircraftId, items).some((m) => m.severity === "ground");
}

export function melGroundsTail(items: MelItem[], aircraftId: string): boolean {
  return hasGroundingMel(aircraftId, items);
}

export function openMelCount(aircraftId: string, items: MelItem[]): number {
  return openMelFor(aircraftId, items).length;
}

export function melBriefForTail(items: MelItem[], aircraftId: string): string {
  const open = openMelFor(aircraftId, items);
  if (!open.length) return "No open MEL / deferred items.";
  const grounds = open.filter((m) => m.severity === "ground").length;
  const defers = open.filter((m) => m.severity === "defer").length;
  const bits = [`${open.length} open`];
  if (grounds) bits.push(`${grounds} grounding`);
  if (defers) bits.push(`${defers} deferred`);
  return bits.join(" · ");
}

export function upsertMelList(items: MelItem[], entry: MelItem): MelItem[] {
  const idx = items.findIndex((m) => m.id === entry.id);
  if (idx === -1) return [entry, ...items];
  return items.map((m, i) => (i === idx ? entry : m));
}
